import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useSignupPage } from './useSignupPage';
import { APP_NAME, PHONE_EXAMPLE_PRETTY, PHONE_PLACEHOLDER } from '../../config/region';
import type { PasswordStrength } from './signupTypes';
import LegalAgreementLine from '../../components/LegalAgreementLine';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const SignupPage: React.FC = () => {
  const {
    formData,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    error,
    loading,
    emailStatus,
    isTouched,
    setIsTouched,
    passwordStrength,
    handleChange,
    handleSubmit,
  } = useSignupPage();

  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  React.useEffect(() => {
    if (isInView) controls.start('visible');
  }, [controls, isInView]);

  return (
    <div className="min-h-screen bg-slate-950">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-[#030b18] to-slate-950 text-white min-h-screen flex items-center"
      >
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
          <motion.div
            className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut', delay: 0.2 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/10 backdrop-blur-sm mb-6"
              >
                <span className="h-2 w-2 rounded-full bg-cyan-400 mr-2"></span>
                Get Started with {APP_NAME}
              </motion.div>
              <motion.h1
                className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <span className="block">Create Your</span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Free Account</span>
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Join thousands of businesses that use our AI-powered sales calling solution to grow their sales pipeline.
              </motion.p>
              <motion.div
                className="flex items-center justify-center lg:justify-start space-x-4 mt-8 lg:mt-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link to="/" className="inline-flex items-center text-sm text-gray-300 hover:text-white">
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to Home
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              ref={ref}
              initial="hidden"
              animate={controls}
              variants={fadeInUp}
              className="bg-white/5 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/10 shadow-lg"
            >
              <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Create Account</h2>
              <form className="space-y-3 sm:space-y-4" onSubmit={handleSubmit}>
                {error && (
                  <div className="bg-red-500/20 border border-red-400 text-red-100 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">{error}</div>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-xs font-medium text-gray-300 mb-0.5">First name</label>
                    <div className="mt-0.5">
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        autoComplete="given-name"
                        required
                        placeholder="John"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-xs font-medium text-gray-300 mb-0.5">Last name</label>
                    <div className="mt-0.5">
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        autoComplete="family-name"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-300 mb-0.5">Email address</label>
                  <div className="mt-1 relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => setIsTouched({ ...isTouched, email: true })}
                      className="w-full px-3 sm:px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 pr-10 sm:pr-12 text-sm"
                      placeholder="you@example.com"
                    />
                    {isTouched.email && (
                      <div className="absolute inset-y-0 right-0 pr-2.5 sm:pr-3 flex items-center">
                        {emailStatus.checking ? (
                          <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : emailStatus.available === true ? (
                          <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                        ) : emailStatus.available === false ? (
                          <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {isTouched.email && emailStatus.available === false && (
                    <p className="mt-1 text-xs sm:text-sm text-red-300">This email is already registered</p>
                  )}
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-gray-300 mb-0.5">Mobile number <span className="text-red-400">*</span></label>
                  <div className="mt-0.5">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={formData.phone || ''}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder={PHONE_PLACEHOLDER}
                    />
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500">Enter a valid phone number, e.g. {PHONE_EXAMPLE_PRETTY}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-xs font-medium text-gray-300 mb-0.5">Password</label>
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-xs text-cyan-400 hover:text-cyan-300">
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setIsTouched({ ...isTouched, password: true })}
                      className="w-full px-3 sm:px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 pr-10 sm:pr-12 text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                  {isTouched.password && (
                    <div className="mt-2 space-y-2">
                      <div className="grid grid-cols-4 gap-2">
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className={`h-1 rounded-full ${
                              passwordStrength.strength >= i
                                ? passwordStrength.isValid ? 'bg-green-400' : 'bg-yellow-400'
                                : 'bg-white/20'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="text-xs text-gray-300 space-y-1">
                        {[
                          { key: 'hasMinLength', label: 'At least 8 characters' },
                          { key: 'hasNumber', label: 'At least one number' },
                          { key: 'hasUpperCase', label: 'At least one uppercase letter' },
                          { key: 'hasLowerCase', label: 'At least one lowercase letter' },
                          { key: 'hasSpecialChar', label: 'At least one special character' }
                        ].map(({ key, label }) => (
                          <p key={key} className={`flex items-center ${passwordStrength[key as keyof PasswordStrength] ? 'text-green-400' : 'text-gray-400'}`}>
                            <CheckCircleIcon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5 ${passwordStrength[key as keyof PasswordStrength] ? 'text-green-400' : 'text-gray-500'}`} />
                            {label}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-300 mb-0.5">Confirm Password</label>
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="text-xs text-cyan-400 hover:text-cyan-300">
                      {showConfirmPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                  {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="mt-1 text-xs sm:text-sm text-red-300">Passwords do not match</p>
                  )}
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={
                      loading ||
                      !formData.email ||
                      !formData.password ||
                      !formData.confirmPassword ||
                      !formData.firstName ||
                      !formData.lastName ||
                      !passwordStrength.isValid ||
                      formData.password !== formData.confirmPassword
                    }
                    className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </button>
                </div>
              </form>
              <div className="mt-6 sm:mt-8 border-t border-white/10 pt-4 sm:pt-6 text-center">
                <p className="text-xs sm:text-sm text-gray-400">
                  Already have an account? <Link to="/login" className="font-medium text-cyan-400 hover:text-cyan-300">Sign in</Link>
                </p>
                <div className="mt-2">
                  <LegalAgreementLine className="text-xs text-gray-500" prefix="By signing up, you agree to our" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default SignupPage;
